import api from '@/api'
import {
	legacyCan,
	legacyCanAccessPage,
	legacyCanTransitionStatus,
	legacyPermissionsPayload
} from '@/utils/permissionLegacy'
import { hasStatusWildcard } from '@/utils/statusTransition'

const state = {
	loginForm: {
		loginId: '',
		password: '',
		keepLogin: true,
	},
	profile: {
		id: '',
		login_id: "",
		password: "",
		name: '',
		email: '',
		permission: "",
		create_datetime: "",
		update_datetime: "",
		delete_datetime: null
	},
	pwForm: {
		email: '',
		password: '',
		newPassword: '',
	},
	master: {},
	testPermissions: {
		full: false,
		byItem: {}
	},
	// 역할 기반 권한 매트릭스 (GET /user/permissions)
	permissions: {
		loaded: false,
		degraded: false,
		version: null,
		role: null,
		pages: [],
		actions: [],
		statusTransitions: []
	}
}

/** 매트릭스 미적재·조회실패·페이지 목록 공백 → 레거시 폴백 */
function useLegacyPermissions(state) {
	const p = state.permissions
	return !!(p.degraded || !p.loaded || !p.pages.length)
}

const getters = {
	loginForm: state => state.loginForm,
	pwForm: state => state.pwForm,
	profile: state => state.profile,
	/** type_permission·dept 기반 시험 카테고리(SOIL, WATER, …) — 서버 프로필에 포함 */
	categories: state => state.profile.categories || [],
	master: state => state.master,
	isAdmin: state => state.profile.permission === 'ADMIN' || state.profile.permission === 'SUB_ADMIN',
	isDeveloper: state => state.profile.id === 0,
	testPermissions: state => state.testPermissions,
	/** 시험항목 작성(=접근) 권한 여부: canWriteTest(testId) */
	canWriteTest: state => (testId) => {
		if (state.testPermissions.full) return true
		// 기술책임자(TECH_ADMIN) 이상은 시험항목 제한 없이 모두 접근 가능
		if (['ADMIN', 'SUB_ADMIN', 'TECH_ADMIN'].includes(state.profile.permission)) return true
		return !!state.testPermissions.byItem[testId]
	},
	/** 시험일지 작성 권한: 역할 게이트(analysis.journal.write) AND 항목 게이트(canWriteTest) */
	canWriteJournal: (state, getters) => (testId) => {
		return getters.can('analysis.journal.write') && getters.canWriteTest(testId)
	},
	/** 역할 기반 권한 원본 */
	permissions: state => state.permissions,
	permissionsLoaded: state => state.permissions.loaded,
	permissionVersion: state => state.permissions.version,
	permissionsDegraded: state => state.permissions.degraded || useLegacyPermissions(state),
	/** 기능(ACTION/PAGE) 허용 여부: can('client.delete') */
	can: state => (featureKey) => {
		if (!featureKey) return false
		const role = state.profile?.permission
		if (useLegacyPermissions(state)) {
			return legacyCan(role, featureKey)
		}
		const p = state.permissions
		return p.actions.includes(featureKey) || p.pages.includes(featureKey)
	},
	/** 페이지 접근 허용 여부: canAccessPage('admin') */
	canAccessPage: state => (pageKey) => {
		if (!pageKey) return true
		const role = state.profile?.permission
		if (useLegacyPermissions(state)) {
			return legacyCanAccessPage(role, pageKey)
		}
		return state.permissions.pages.includes(pageKey)
	},
	/** 상태 전이 허용 여부: canTransitionStatus(from, to, event). event 생략 시 from>to 중 하나라도 허용이면 true */
	canTransitionStatus: state => (from, to, event) => {
		const role = state.profile?.permission
		if (useLegacyPermissions(state)) {
			return legacyCanTransitionStatus(role, from, to, event)
		}
		const list = state.permissions.statusTransitions
		// 2026-08-20: ADMIN/SUB_ADMIN 하드코딩 예외를 «모든 단계 변경 허용»
		// 와일드카드로 대체했다. 어느 역할에 줄지는 세부 권한 설정이 정한다.
		if (hasStatusWildcard(list)) return true
		if (event) return list.includes(`project.status:${from}>${to}:${event}`)
		const prefix = `project.status:${from}>${to}:`
		return list.some(k => k.startsWith(prefix))
	},
	/**
	 * 상태 드롭다운용 전이 키 목록.
	 * 폴백 시 레거시 전이표, 정상 시 매트릭스 statusTransitions.
	 */
	effectiveStatusTransitions: state => {
		const role = state.profile?.permission
		if (useLegacyPermissions(state) || !(state.permissions.statusTransitions || []).length) {
			return legacyPermissionsPayload(role).statusTransitions
		}
		return state.permissions.statusTransitions
	}
}

const mutations = {
	setLoginForm: (state, data) => {
		state.loginForm = {...data}
	},
	setPwForm: (state, data) => {
		state.pwForm = {...data}
	},
	setProfile: (state, data) => {
		state.profile = {...data}
	},
	/**
	 * 사용자 세션 상태 초기화.
	 * 리로드 없이 다른 계정으로 로그인하면 이전 사용자의 profile·권한이 그대로 남아
	 * 그 권한으로 화면이 그려지므로, 로그인 시점에 반드시 비운다.
	 */
	resetUserSession: (state) => {
		state.profile = {
			id: '',
			login_id: '',
			password: '',
			name: '',
			email: '',
			permission: '',
			create_datetime: '',
			update_datetime: '',
			delete_datetime: null
		}
		state.master = {}
		state.testPermissions = { full: false, byItem: {} }
		state.permissions = {
			loaded: false,
			degraded: false,
			version: null,
			role: null,
			pages: [],
			actions: [],
			statusTransitions: []
		}
	},
	setMasterInfo: (state, data) => {
		state.master = {...data}
	},
	setTestPermissions: (state, data) => {
		state.testPermissions = {
			full: !!data.full,
			byItem: data.byItem || {}
		}
	},
	setPermissions: (state, data) => {
		state.permissions = {
			loaded: true,
			degraded: !!data?.degraded,
			version: data?.version ?? null,
			role: data?.role ?? null,
			pages: Array.isArray(data?.pages) ? data.pages : [],
			actions: Array.isArray(data?.actions) ? data.actions : [],
			statusTransitions: Array.isArray(data?.statusTransitions) ? data.statusTransitions : []
		}
	},
	setPermissionsDegraded: (state, role) => {
		const payload = legacyPermissionsPayload(role || state.profile?.permission)
		state.permissions = {
			loaded: true,
			degraded: true,
			version: state.permissions.version,
			role: role || state.profile?.permission || null,
			pages: payload.pages,
			actions: payload.actions,
			statusTransitions: payload.statusTransitions
		}
	}
}

let profileRequest = null
let testPermissionsRequest = null
let permissionsRequest = null

/** 모듈 레벨 in-flight 캐시까지 함께 비운다 (스토어 초기화만으로는 남는다) */
function clearSessionCaches() {
	profileRequest = null
	testPermissionsRequest = null
	permissionsRequest = null
}

const actions = {
	/** 다른 계정으로 재로그인해도 이전 사용자의 권한이 남지 않도록 세션을 비운다 */
	clearSession: ({ commit }) => {
		clearSessionCaches()
		commit('resetUserSession')
	},
	login: async ({commit, dispatch, state}, params) => {
		const res = await api.user.login(params);
		// 로그인 성공 시 이전 세션 흔적 제거.
		// 로그아웃은 location.href 로 전체 리로드되지만, /login 으로 직접 이동하거나
		// 가드가 next('/login') 을 태우는 경로는 리로드가 없어 이전 사용자의
		// profile·권한이 남고, getProfile 이 state.profile.id 로 조기 반환해 그대로 쓰인다.
		// (실패 응답에서는 기존 세션을 건드리지 않는다)
		if (res && res.data && res.data.token) {
			dispatch('clearSession')
		}
		return res;
	},
	autoLogin: async({commit, state}) => {
		const res = await api.user.autoLogin()
		return res;
	},
	resetPw: async ({commit, state}) => {
		const res = await api.user.resetPw(state.pwForm)
		return res;
	},
	getProfile: async ({ commit, dispatch, state }) => {
		if (state.profile?.id) return state.profile

		if (!profileRequest) {
			profileRequest = api.user.getProfile()
				.then(res => {
					commit('setProfile', res)
					dispatch('loadTestPermissions')
					dispatch('loadPermissions')
					return res
				})
				.catch(err => {
					profileRequest = null
					throw err
				})
		}

		return profileRequest
	},
	reloadProfile: async ({ commit, dispatch }) => {
		profileRequest = null
		const res = await api.user.getProfile()
		commit('setProfile', res)
		await Promise.all([
			dispatch('reloadTestPermissions'),
			dispatch('reloadPermissions')
		])
		return res
	},
	loadTestPermissions: async ({ commit, state }) => {
		if (state.testPermissions.full
			|| Object.keys(state.testPermissions.byItem).length > 0) {
			return state.testPermissions
		}
		if (!testPermissionsRequest) {
			testPermissionsRequest = api.user.getTestPermissions()
				.then(res => {
					commit('setTestPermissions', res)
					return res
				})
				.catch(err => {
					testPermissionsRequest = null
					throw err
				})
		}
		return testPermissionsRequest
	},
	reloadTestPermissions: async ({ commit }) => {
		testPermissionsRequest = null
		const res = await api.user.getTestPermissions()
		commit('setTestPermissions', res)
		return res
	},
	// 역할 기반 권한 로드 (getProfile 이후 자동 호출). 중복 호출 방지.
	loadPermissions: async ({ commit, state }) => {
		if (state.permissions.loaded) return state.permissions
		if (!permissionsRequest) {
			permissionsRequest = api.user.getPermissions()
				.then(res => {
					commit('setPermissions', res)
					return res
				})
				.catch(err => {
					permissionsRequest = null
					console.warn('[permission] loadPermissions failed → degraded fallback', err?.message || err)
					commit('setPermissionsDegraded', state.profile?.permission)
					return state.permissions
				})
		}
		return permissionsRequest
	},
	// 권한 강제 재조회 (매트릭스 변경 반영)
	reloadPermissions: async ({ commit, state }) => {
		permissionsRequest = null
		try {
			const res = await api.user.getPermissions()
			commit('setPermissions', res)
			return res
		} catch (err) {
			console.warn('[permission] reloadPermissions failed → degraded fallback', err?.message || err)
			commit('setPermissionsDegraded', state.profile?.permission)
			return state.permissions
		}
	},
	// 캐시 버전 비교 후 변경 시에만 재조회 (라우트 진입 시 사용)
	syncPermissionVersion: async ({ commit, dispatch, state }) => {
		try {
			if (!state.permissions.loaded) {
				return dispatch('loadPermissions')
			}
			const version = await api.user.getPermissionVersion()
			if (version != null && version !== state.permissions.version) {
				return dispatch('reloadPermissions')
			}
			return state.permissions
		} catch (e) {
			if (!state.permissions.loaded || !state.permissions.pages.length) {
				console.warn('[permission] syncPermissionVersion failed → degraded fallback', e?.message || e)
				commit('setPermissionsDegraded', state.profile?.permission)
			}
			return state.permissions
		}
	},
	getSignImg: async({commit, state}, id) => {
		const res = await api.user.getUserSignImg(id);

		function blobToBase64(blob) {
			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onloadend = () => resolve(reader.result);
				reader.onerror = reject;
				reader.readAsDataURL(blob);
			});
		}

		const base64String = await blobToBase64(res.data);
        return base64String;
	},
	getMasterInfo: async({commit, state}, type) => {
		const res = await api.user.getMasterInfo(type)
		commit('setMasterInfo', res)
	},
	updateProfile: async ({ dispatch, state }, payload) => {
		const profileBody = { rank: payload.rank ?? null }
		if (state.profile?.permission === 'ADMIN') {
			profileBody.name = payload.name
			profileBody.email = payload.email
			profileBody.deptId = payload.dept_id ?? payload.deptId ?? null
			profileBody.permission = payload.permission
		}
		await api.user.editProfile(profileBody)
		if (payload.newPassword) {
			await api.user.editPassword({ newPassword: payload.newPassword })
		}
		await dispatch('reloadProfile')
		return true
	}
}

export default { namespaced: true, state, getters, mutations, actions }
